/**
 * AudioAnalysis page
 */

// React
import React, { FC } from 'react';

// Material
import { Table, TableBody, TableCell, TableRow, Box } from '@mui/material';

// Components
import SelectBase from 'components/Select';
import TableHeader from 'components/TableHeader';
import SelectInput from 'components/SelectInput';
import SearchInput from 'components/SearchInput';
import TotalRecords from 'components/TotalRecords';
import ProcessButton from 'components/ProcessButton';
import PaginationComponent from 'components/Pagination';

// Data mock
import {
  menuItems,
  headerAudioAnalysis,
  systemUsageDigitalStatus,
  defaultParamsAudioAnalysis,
} from 'initDataPages';

// Common styles
import {
  BpIcon,
  BoxNoDataList,
  PageContainer,
  BpCheckedIcon,
  CustomTableHead,
  RecordsContainer,
  CustomTableContainer,
  WrapperSearchAndButton,
  WrapperSearchAndBtnProcess,
} from 'styles';

// Hooks
import useGetListData from 'hooks/useGetListData';

// Services
import AudioAnalysisService from 'services/AudioAnalysisService';

// Utils
import { DataAudioAnalysis } from 'utils/types';
import { formatDate } from 'utils/helper/common';
import { DEFAULT_DURATION, NOTIFICATION_MESSAGES, SYSTEM_MENU, VALUE_MENU } from 'utils/constants';

// Component
const AudioAnalysis: FC = () => {
  const {
    perPage,
    status,
    dataList,
    isLoading,
    totalPages,
    currentPage,
    totalRecords,
    paramsGetList,
    scrollContainerRef,
    getIndex,
    isSortable,
    handleExport,
    handleChangePage,
    handleRequestSort,
    handleSelectStatus,
  } = useGetListData<DataAudioAnalysis>(
    (paramsGetList) => AudioAnalysisService.getAudioAnalysis(paramsGetList),
    (paramsGetList) => AudioAnalysisService.exportAudioAnalysis(paramsGetList),
    undefined,
    defaultParamsAudioAnalysis,
    SYSTEM_MENU.AUDIO_ANALYSIS,
    NOTIFICATION_MESSAGES.ERROR.DOWNLOAD
  );

  return (
    <PageContainer>
      <SelectInput menuItems={menuItems} defaultValue={VALUE_MENU.AUDIO_ANALYSIS} />
      <RecordsContainer type="primary">
        <TotalRecords
          currentPage={currentPage}
          recordsPerPage={perPage}
          totalRecords={totalRecords}
        />
        <WrapperSearchAndButton type="primary">
          <SelectBase
            value={status}
            onChange={handleSelectStatus}
            sx={{ width: 145, mr: '26px' }}
            sxSelect={{ height: 37, fontSize: '16px' }}
            sxSelectItem={{ fontSize: '14px', padding: '10px' }}
            menuItems={systemUsageDigitalStatus}
          />
          <WrapperSearchAndBtnProcess>
            <SearchInput />
            <ProcessButton disabled={!totalRecords} handleDownload={handleExport} />
          </WrapperSearchAndBtnProcess>
        </WrapperSearchAndButton>
      </RecordsContainer>
      <CustomTableContainer>
        <Box className="table-container" ref={scrollContainerRef}>
          <Table aria-label="register user table">
            <CustomTableHead>
              <TableHeader
                isSortable={isSortable}
                header={headerAudioAnalysis}
                paramsGetList={paramsGetList}
                handleRequestSort={handleRequestSort}
              />
            </CustomTableHead>
            <TableBody>
              {dataList && dataList.length > 0
                ? dataList?.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell align="center">{getIndex(index)}</TableCell>
                      <TableCell align="center">
                        {row?.status === 'in-used' ? <BpCheckedIcon /> : <BpIcon />}
                      </TableCell>
                      <TableCell align="left">{row?.user_company ?? ''}</TableCell>
                      <TableCell align="left">{row?.user_department ?? ''}</TableCell>
                      <TableCell align="left">{row?.user_name ?? ''}</TableCell>
                      <TableCell align="left">{row?.user_email ?? ''}</TableCell>
                      <TableCell align="left">{row?.event_name}</TableCell>
                      <TableCell align="left">{formatDate(row?.event_start_date) ?? ''}</TableCell>
                      <TableCell align="left">{formatDate(row?.event_end_date) ?? ''}</TableCell>
                      <TableCell align="left">{row?.staff_count ?? '0'}</TableCell>
                      <TableCell align="left">{row?.recordings_count ?? '0'}</TableCell>
                      <TableCell align="left">{row?.total_duration ?? DEFAULT_DURATION}</TableCell>
                    </TableRow>
                  ))
                : null}
            </TableBody>
          </Table>
          {dataList?.length === 0 && !isLoading && (
            <BoxNoDataList>{NOTIFICATION_MESSAGES.ERROR.SEARCH}</BoxNoDataList>
          )}
        </Box>
      </CustomTableContainer>
      <PaginationComponent
        totalPages={totalPages}
        currentPage={currentPage}
        handleChangePage={handleChangePage}
        dataLength={totalRecords}
      />
    </PageContainer>
  );
};

export default AudioAnalysis;
